import React from "react";
import { Location, Link } from "react-router-dom";
import { Box, Button, SxProps, Tooltip } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { CommentTextIcon, NewTabIcon } from "assets";
import { getDocumentBills, UtilityBillDTO } from "api";
import {
  capitalizeString,
  getInitials,
  getDateFormatset,
  setToLocalStorage,
  toUSNumber,
} from "utils";
import { Avatar, UtilityBillTypeKey } from "components";
import { TABLE_HEADERS, EMPTY_CELL_VALUE } from "consts";
import { BillAttachedDocsButton } from "pages/authenticated/documents/components";

import { EmissionCell } from "./emission-cell";
import { AnomalyLogCell } from "./anomaly-log-cell";

import { actionColumnStyles, commonColumnStyles } from "../../styles";
import { UTILITY_BILL_PAGE } from "../../consts";

/**
 * Tracks the most recent valid date typed by the user for each date filter column.
 * Written synchronously in onChange so onKeyDown(Enter) always reads the current value,
 * bypassing the async React state update in column.setFilterValue.
 */
const pendingDateFilters = new Map<string, string | undefined>();

export const commonCellRenderer = <T extends any>(
  { cell }: any,
  tooltip: "hasTooltip" | "noTooltip" = "noTooltip",
  customBoxRenderer?: React.ReactElement,
  customTooltip?: React.ReactElement | string,
  sx?: SxProps,
): React.ReactNode => {
  const value = cell.getValue() as T;

  if (value !== 0 && value !== "0" && !value) {
    return (
      <Box
        sx={sx ? { ...commonColumnStyles, ...(sx as any) } : commonColumnStyles}
      >
        {" "}
        {EMPTY_CELL_VALUE}{" "}
      </Box>
    );
  }

  if (tooltip === "hasTooltip" && (!customBoxRenderer || customTooltip)) {
    return (
      <Tooltip title={customTooltip ?? (value as string)} placement="top">
        <Box
          sx={
            sx ? { ...commonColumnStyles, ...(sx as any) } : commonColumnStyles
          }
        >
          {" "}
          {value as string}{" "}
        </Box>
      </Tooltip>
    );
  }

  if (tooltip === "hasTooltip" && customBoxRenderer) {
    return (
      <Tooltip title={value as string} placement="top">
        {customBoxRenderer}
      </Tooltip>
    );
  }

  return (
    customBoxRenderer || (
      <Box
        sx={sx ? { ...commonColumnStyles, ...(sx as any) } : commonColumnStyles}
      >
        {" "}
        {value as string}{" "}
      </Box>
    )
  );
};

const columnHeader = (text?: string) => (
  <Box sx={{ height: "30px", display: "flex", alignItems: "center" }}>
    {text}
  </Box>
);

// ---- Table Column
export const getBillEntriesColums = ({
  currentUrl,
  onApplyDateFilter,
  onSupportingDocsChange,
}: {
  currentUrl: Location;
  onApplyDateFilter?: (id: string, value: string | undefined) => void;
  onSupportingDocsChange?: () => void;
}) => [
  {
    id: "common-item",
    header: "",
    Header: columnHeader(),
    columns: [
      {
        id: "comments",
        header: "",
        accessorKey: "numberOfComments",
        enableColumnActions: false,
        enableColumnFilter: false,
        enableResizing: false,
        size: 80,
        Cell: ({ row }: any) => {
          const handleClick = (e: any) => {
            e.stopPropagation();
            setToLocalStorage("dataEntries", JSON.stringify(row));
            setToLocalStorage("type", "Summary");
            window.sessionStorage.setItem("summaryDocumentID", row.original.id);
            getDocumentBills(row.original.documentGuid)
              .then((bills) => {
                setToLocalStorage("documentbills", JSON.stringify(bills));
              })
              .catch((error) => {
                console.error("Error fetching document bills:", error);
              });
            setToLocalStorage(
              "Summary_URL",
              currentUrl.pathname + currentUrl.search,
            );
            setToLocalStorage("documentIdForBills", row.original.id);
          };
          return !row.original.isEstimate ? (
            <>
              <Tooltip title="Open bill in a new tab" placement="top">
                <Link
                  to={`${UTILITY_BILL_PAGE.ABSOLUTE_PATH(row.original.id)}`}
                  target="_blank"
                >
                  <Button sx={actionColumnStyles} onClick={handleClick}>
                    <NewTabIcon />
                  </Button>
                </Link>
              </Tooltip>
              <Tooltip title="Open comments" placement="top">
                <Link
                  to={`${UTILITY_BILL_PAGE.ABSOLUTE_PATH(
                    row.original.id,
                  )}/comments`}
                >
                  <Button sx={actionColumnStyles} onClick={handleClick}>
                    <CommentTextIcon />
                  </Button>
                </Link>
              </Tooltip>
            </>
          ) : (
            <Box sx={commonColumnStyles}>{EMPTY_CELL_VALUE}</Box>
          );
        },
      },
      {
        Header: columnHeader("Customer Site ID"),
        header: "Customer Site ID",
        accessorKey: "customerSiteId",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        Header: columnHeader("Business Unit"),
        accessorKey: "businessUnit",
        header: "Business Unit",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: "Site",
        Header: columnHeader("Site"),
        accessorKey: "siteName",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: "Type",
        Header: columnHeader("Type"),
        accessorKey: "type",
        size: 180,
        Cell: ({ cell }: any) =>
          commonCellRenderer<UtilityBillTypeKey>({ cell }, "hasTooltip"),
      },
      {
        header: "subType",
        Header: columnHeader("Sub-Type"),
        accessorKey: "subtype",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "noTooltip"),
      },
      {
        header: "Provider",
        Header: columnHeader("Source"),
        accessorKey: "provider",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: TABLE_HEADERS.accountNumber,
        Header: columnHeader(TABLE_HEADERS.accountNumber),
        accessorKey: "accountNumber",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: TABLE_HEADERS.meterNumber,
        Header: columnHeader(TABLE_HEADERS.meterNumber),
        accessorKey: "meterNumber",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: "GHG Emission",
        Header: columnHeader("GHG Emission"),
        accessorKey: "scope",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        accessorKey: "addedBy",
        Header: columnHeader("Added By"),
        header: TABLE_HEADERS.uploader,
        size: 180,
        Cell: ({ cell }: any) =>
          commonCellRenderer<UtilityBillDTO["addedBy"]>(
            { cell },
            "hasTooltip",
            <Box>
              <Avatar
                sx={{
                  width: "32px",
                  height: "32px",
                  paddingLeft: "5px",
                }}
              >
                {getInitials(cell.getValue())}
              </Avatar>
            </Box>,
          ),
      },
      {
        id: "consumptionStartDate",
        header: "Start date",
        Header: columnHeader("Start date"),
        accessorKey: "startDate",
        enableColumnFilter: true,
        size: 180,
        Filter: ({ column }: any) => {
          const raw = column.getFilterValue();
          const dateValue =
            raw === undefined || raw === null
              ? null
              : dayjs.isDayjs(raw)
              ? raw
              : dayjs(raw);

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateValue}
                onChange={(newValue: any) => {
                  if (!newValue) {
                    pendingDateFilters.set("consumptionStartDate", undefined);
                    column.setFilterValue(undefined);
                  } else if (dayjs.isDayjs(newValue) && newValue.isValid()) {
                    const formatted = newValue.format("YYYY-MM-DD");
                    pendingDateFilters.set("consumptionStartDate", formatted);
                    column.setFilterValue(formatted);
                  }
                }}
                onAccept={(newValue: any) => {
                  const formatted = newValue
                    ? dayjs(newValue).format("YYYY-MM-DD")
                    : undefined;
                  pendingDateFilters.set("consumptionStartDate", formatted);
                  column.setFilterValue(formatted);
                  onApplyDateFilter?.("consumptionStartDate", formatted);
                }}
                format={getDateFormatset()}
                slotProps={{
                  textField: {
                    sx: { minWidth: "120px" },
                    variant: "standard",
                    onKeyDown: (e: any) => {
                      if (e.key === "Enter") {
                        const value =
                          pendingDateFilters.get("consumptionStartDate") ??
                          (column.getFilterValue() as string | undefined);
                        if (value)
                          onApplyDateFilter?.("consumptionStartDate", value);
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        filterFn: (row: any, id: any, currentFilterValue: any) => {
          if (currentFilterValue === undefined || currentFilterValue === null) {
            return true;
          }
          const filterDate = dayjs(currentFilterValue).startOf("day");
          const rowValue = row.getValue(id);
          if (rowValue === undefined || rowValue === null) {
            return false;
          }
          const rowDate = dayjs(rowValue).startOf("day");
          return filterDate.valueOf() <= rowDate.valueOf();
        },
        Cell: ({ cell }: any) => {
          const value = cell.getValue();
          return commonCellRenderer(
            { cell },
            "noTooltip",
            value ? (
              <Box sx={commonColumnStyles}>
                {dayjs(value as string).format(getDateFormatset())}
              </Box>
            ) : undefined,
          );
        },
      },
      {
        id: "consumptionEndDate",
        header: "End date",
        Header: columnHeader("End date"),
        accessorKey: "endDate",
        enableColumnFilter: true,
        size: 180,
        Filter: ({ column }: any) => {
          const raw = column.getFilterValue();
          const dateValue =
            raw === undefined || raw === null
              ? null
              : dayjs.isDayjs(raw)
              ? raw
              : dayjs(raw);

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateValue}
                onChange={(newValue: any) => {
                  if (!newValue) {
                    pendingDateFilters.set("consumptionEndDate", undefined);
                    column.setFilterValue(undefined);
                  } else if (dayjs.isDayjs(newValue) && newValue.isValid()) {
                    const formatted = newValue.format("YYYY-MM-DD");
                    pendingDateFilters.set("consumptionEndDate", formatted);
                    column.setFilterValue(formatted);
                  }
                }}
                onAccept={(newValue: any) => {
                  const formatted = newValue
                    ? dayjs(newValue).format("YYYY-MM-DD")
                    : undefined;
                  pendingDateFilters.set("consumptionEndDate", formatted);
                  column.setFilterValue(formatted);
                  onApplyDateFilter?.("consumptionEndDate", formatted);
                }}
                format={getDateFormatset()}
                slotProps={{
                  textField: {
                    sx: { minWidth: "120px" },
                    variant: "standard",
                    onKeyDown: (e: any) => {
                      if (e.key === "Enter") {
                        const value =
                          pendingDateFilters.get("consumptionEndDate") ??
                          (column.getFilterValue() as string | undefined);
                        if (value)
                          onApplyDateFilter?.("consumptionEndDate", value);
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        filterFn: (row: any, id: any, currentFilterValue: any) => {
          if (currentFilterValue === undefined || currentFilterValue === null) {
            return true;
          }
          const filterDate = dayjs(currentFilterValue).startOf("day");
          const rowValue = row.getValue(id);
          if (rowValue === undefined || rowValue === null) {
            return false;
          }
          const rowDate = dayjs(rowValue).startOf("day");
          return rowDate.valueOf() <= filterDate.valueOf();
        },
        Cell: ({ cell }: any) => {
          const value = cell.getValue();
          return commonCellRenderer(
            { cell },
            "noTooltip",
            value ? (
              <Box sx={commonColumnStyles}>
                {dayjs(value as string).format(getDateFormatset())}
              </Box>
            ) : undefined,
          );
        },
      },
      {
        header: "Number of Days",
        Header: columnHeader("Number of Days"),
        accessorKey: "daysCovered",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: "Usage",
        Header: columnHeader("Usage"),
        accessorKey: "measuredValue",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const { unitCalculationFormula, measuredUnit, measuredValue } =
            row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <EmissionCell
              value={measuredValue}
              explanation={unitCalculationFormula}
              unit={measuredUnit}
              unitValueShow
            />,
          );
        },
      },
      {
        header: "Standardized Usage Value",
        Header: columnHeader("Standardized Usage Value"),
        accessorKey: "standardizedUsageValue",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { standardizedUsageUnit } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>
              {toUSNumber(value as string)} {standardizedUsageUnit}
            </Box>,
          );
        },
      },
      {
        header: "Location Based Emissions",
        Header: columnHeader("Location Based Emissions"),
        accessorKey: "co2Emission",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();

          const locationEmissionFactorValue =
            row.original.locationBasedStandardizedEmissionFactorValue ?? "";
          const locationEmissionFactorUnit =
            row.original.locationBasedStandardizedEmissionFactorUnit ?? "";

          const explanationToAdd = `\n\nLocation CO2e EF: ${locationEmissionFactorValue} ${locationEmissionFactorUnit}`;

          const explanation =
            String(row.original.explanation ?? "") + explanationToAdd;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>
              <EmissionCell value={value} explanation={explanation} />
            </Box>,
          );
        },
      },
    ],
  },
  {
    id: "location-renewable-tab",
    header: "Location",
    Header: columnHeader("Location"),
    columns: [
      {
        Header: columnHeader("Renewable Usage"),
        header: "Renewable Usage",
        accessorKey: "locationRenewableUsage",
        enableGrouping: false,
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { standardizedUsageUnit } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${toUSNumber(
              value,
            )} ${standardizedUsageUnit}`}</Box>,
          );
        },
      },
      {
        Header: columnHeader("Non-Renewable Usage"),
        header: "Non-Renewable Usage",
        accessorKey: "locationNonRenewableUsage",
        enableGrouping: false,
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { standardizedUsageUnit } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${toUSNumber(
              value,
            )} ${standardizedUsageUnit}`}</Box>,
          );
        },
      },
    ],
  },
  {
    id: "market-based-tab",
    header: "",
    Header: columnHeader(),
    columns: [
      {
        header: "Market Based Emissions",
        Header: (
          <Box sx={{ height: "30px", display: "flex", alignItems: "center" }}>
            Market Based Emissions
          </Box>
        ),
        accessorKey: "marketBasedEmission",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();

          const marketEmissionFactorValue =
            row.original.marketBasedStandardizedEmissionFactorValue ?? "";
          const marketEmissionFactorUnit =
            row.original.marketBasedStandardizedEmissionFactorUnit ?? "";

          const explanationToAdd = `\n\nMarket CO2e EF: ${marketEmissionFactorValue} ${marketEmissionFactorUnit}`;

          const explanation =
            String(row.original.marketExplanation ?? "") + explanationToAdd;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>
              <EmissionCell value={value} explanation={explanation} />
            </Box>,
          );
        },
      },
    ],
  },
  {
    id: "market-renewable-tab",
    header: "Market",
    Header: columnHeader("Market"),
    columns: [
      {
        header: "Renewable Usage",
        Header: columnHeader("Renewable Usage"),
        accessorKey: "marketRenewableUsage",
        enableGrouping: false,
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { standardizedUsageUnit } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${toUSNumber(
              value,
            )} ${standardizedUsageUnit}`}</Box>,
          );
        },
      },
      {
        header: "Non-Renewable Usage",
        Header: columnHeader("Non-Renewable Usage"),
        accessorKey: "marketNonRenewableUsage",
        enableGrouping: false,
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { standardizedUsageUnit } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${toUSNumber(
              value,
            )} ${standardizedUsageUnit}`}</Box>,
          );
        },
      },
    ],
  },
  {
    id: "common-item-2",
    header: "",
    Header: columnHeader(),
    columns: [
      {
        header: "Cost",
        Header: columnHeader("Cost"),
        accessorKey: "amount",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { currency } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${currency} ${toUSNumber(
              value,
            )}`}</Box>,
          );
        },
      },
      {
        id: "createDate",
        header: "Date Created",
        Header: columnHeader("Date Created"),
        accessorKey: "createDate",
        enableColumnFilter: true,
        size: 180,
        Filter: ({ column }: any) => {
          const raw = column.getFilterValue();
          const dateValue =
            raw === undefined || raw === null
              ? null
              : dayjs.isDayjs(raw)
              ? raw
              : dayjs(raw);

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateValue}
                onChange={(newValue: any) => {
                  if (!newValue) {
                    pendingDateFilters.set("createDate", undefined);
                    column.setFilterValue(undefined);
                  } else if (dayjs.isDayjs(newValue) && newValue.isValid()) {
                    const formatted = newValue.format("YYYY-MM-DD");
                    pendingDateFilters.set("createDate", formatted);
                    column.setFilterValue(formatted);
                  }
                }}
                onAccept={(newValue: any) => {
                  const formatted = newValue
                    ? dayjs(newValue).format("YYYY-MM-DD")
                    : undefined;
                  pendingDateFilters.set("createDate", formatted);
                  column.setFilterValue(formatted);
                  onApplyDateFilter?.("createDate", formatted);
                }}
                format={getDateFormatset()}
                slotProps={{
                  textField: {
                    sx: { minWidth: "120px" },
                    variant: "standard",
                    onKeyDown: (e: any) => {
                      if (e.key === "Enter") {
                        const value =
                          pendingDateFilters.get("createDate") ??
                          (column.getFilterValue() as string | undefined);
                        if (value) onApplyDateFilter?.("createDate", value);
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        Cell: ({ cell }: any) => {
          const value = cell.getValue();
          return commonCellRenderer(
            { cell },
            "noTooltip",
            value ? (
              <Box sx={commonColumnStyles}>
                {dayjs(value as string).format(getDateFormatset())}
              </Box>
            ) : undefined,
          );
        },
      },
      {
        header: "Last Modified Date",
        Header: columnHeader("Last Modified Date"),
        accessorKey: "updateDate",
        size: 180,
        Filter: ({ column }: any) => {
          const raw = column.getFilterValue();
          const dateValue =
            raw === undefined || raw === null
              ? null
              : dayjs.isDayjs(raw)
              ? raw
              : dayjs(raw);

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateValue}
                onChange={(newValue: any) => {
                  const formatted = newValue
                    ? dayjs(newValue).format("YYYY-MM-DD")
                    : undefined;
                  column.setFilterValue(formatted);
                  onApplyDateFilter?.("updateDate", formatted);
                }}
                format={getDateFormatset()}
                slotProps={{
                  textField: {
                    sx: { minWidth: "120px" },
                    variant: "standard",
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        Cell: ({ cell }: any) => {
          const value = cell.getValue();
          return commonCellRenderer(
            { cell },
            "noTooltip",
            value ? (
              <Box sx={commonColumnStyles}>
                {dayjs(value as string).format(getDateFormatset())}
              </Box>
            ) : undefined,
          );
        },
      },
      {
        header: "Calculation Method",
        Header: columnHeader("Calculation Method"),
        accessorKey: "calculationMethod",
        filterFn: "includesString",
        size: 180,
        Cell: ({ cell, row }: any) => {
          const { billType, calculationMethod, interExtrapolate } =
            row.original;

          if (!interExtrapolate) {
            return <Box sx={commonColumnStyles}>{EMPTY_CELL_VALUE}</Box>;
          }

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>{`${capitalizeString(
              billType,
            )} - ${capitalizeString(calculationMethod)}`}</Box>,
          );
        },
      },
      {
        header: "Edited",
        Header: columnHeader("Edited"),
        accessorKey: "edited",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Manually Approved",
        Header: columnHeader("Manually Approved"),
        accessorKey: "manuallyApproved",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Duplicate Warning",
        Header: columnHeader("Duplicate Warning"),
        accessorKey: "duplicateWarning",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Sequential Threshold Consumption Last Period",
        Header: columnHeader("Sequential Threshold Consumption Last Period"),
        accessorKey: "thresholdExceededConsumptionLastPeriod",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Sequential Threshold Cost Last Period",
        Header: columnHeader("Sequential Threshold Cost Last Period"),
        accessorKey: "thresholdExceededCostLastPeriod",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Sequential Threshold Last Year",
        Header: columnHeader("Sequential Threshold Last Year"),
        accessorKey: "thresholdExceededLastYear",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Estimate Source",
        Header: columnHeader("Estimate Source"),
        accessorKey: "estimateSource",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        header: "Unit Cost",
        Header: columnHeader("Unit Cost"),
        accessorKey: "unitCost",
        size: 180,
        Cell: ({ cell }: any) =>
          commonCellRenderer(
            { cell },
            "hasTooltip",
            undefined,
            "Standardized Cost (USD) / Standardized Usage",
          ),
      },
      {
        header: "Usage Per Unit Area",
        Header: columnHeader("Usage Per Unit Area"),
        accessorKey: "usagePerUnitArea",
        size: 180,
        Cell: ({ cell }: any) => commonCellRenderer({ cell }, "hasTooltip"),
      },
      {
        header: "Bill Superseded",
        Header: columnHeader("Bill Superseded"),
        accessorKey: "billSuperceeded",
        size: 180,
        Cell: ({ row }: any) => {
          const { billSuperceeded, billSuperceededDetails } = row.original;

          if (!billSuperceeded) {
            return <Box sx={commonColumnStyles}>{EMPTY_CELL_VALUE}</Box>;
          }

          return <Box sx={commonColumnStyles}>{billSuperceededDetails}</Box>;
        },
      },
      {
        header: "Bill ID",
        Header: columnHeader("Bill ID"),
        accessorKey: "id",
        size: 180,
        Cell: commonCellRenderer,
      },
      {
        id: "supportingDocs",
        header: "Docs",
        Header: columnHeader("Docs"),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableBodyCellProps: {
          onClick: (e: any) => e.stopPropagation(),
        },
        Cell: ({ row }: any) => (
          <BillAttachedDocsButton
            billId={String(row.original.id)}
            documentCount={(row.original as any)?.supportingFilesCount ?? 0}
            onDocumentsChanged={onSupportingDocsChange}
          />
        ),
      },
      {
        header: "Data Entries Status",
        Header: columnHeader("Data Entries Status"),
        accessorKey: "statusFilter",
        size: 180,
        Cell: commonCellRenderer,
      },
    ],
  },
  {
    id: "anomalies",
    header: "",
    Header: columnHeader(),
    columns: [
      {
        header: "Anomaly log",
        Header: columnHeader("Anomaly log"),
        accessorKey: "anomalies",
        enableSorting: false,
        size: 180,
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue();
          const { manuallyApproved, updateDate } = row.original;

          return commonCellRenderer(
            { cell },
            "noTooltip",
            <Box sx={commonColumnStyles}>
              <AnomalyLogCell
                anomalies={value}
                updateDate={updateDate}
                isManuallyApproved={manuallyApproved}
              />
            </Box>,
          );
        },
      },
    ],
  },
];

/**
 * Injects muiTableHeadCellFilterTextFieldProps (e.g. arrow button, Enter to apply) into each leaf column
 * that uses the default filter input. Required when using column groups so the table-level props are applied.
 */
export const injectFilterTextFieldProps = <T extends Record<string, unknown>>(
  columnGroups: T[],
  filterTextFieldProps: Record<string, unknown> | undefined,
): T[] => {
  if (!filterTextFieldProps) return columnGroups;

  const injectOne = (col: any): any => {
    if (col.columns) {
      return { ...col, columns: col.columns.map((c: any) => injectOne(c)) };
    }
    if (col.enableColumnFilter === false || col.Filter) return col;
    return {
      ...col,
      muiTableHeadCellFilterTextFieldProps: filterTextFieldProps,
      muiFilterTextFieldProps: filterTextFieldProps,
    };
  };

  return columnGroups.map((group) => injectOne(group)) as T[];
};
